import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Badge,
    Banner,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Checkbox,
    ConfirmDialog,
    DateTime,
    DetailRow,
    DownloadIcon as Download,
    RevealIcon as Eye,
    FieldGrid,
    Label,
    SearchableSelect,
    StatusBadge,
    Textarea,
    Timeline,
    type TimelineItem,
} from '@bemor/web-kit/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type {
    MerchantBankAccount,
    MerchantReview,
    MerchantReviewRequest,
    OnboardingDocument,
    OnboardingHistoryEntry,
    OnboardingOwner,
    OnboardingReveal,
    StaffOnboardingApplication,
} from '@/api/types';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, useStaffDirectory, whoName } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';

/*
 * BM-59: the staff review of an onboarding application on the merchant page (design 0.4.0 `backoffice/merchant`
 * plus what BM-40 added): the application with its sensitive values masked and an audited Reveal, the documents
 * with Download, Accept and Reject, the Request info dialog with the step and the document kinds, and the review
 * history. The acquirer reviewer (merchants read only) sees it all read-only.
 */

export const REVIEWABLE = ['submitted', 'in_review', 'needs_info'] as const;
export const isReviewable = (state: string) =>
    (REVIEWABLE as readonly string[]).includes(state);

const STEP_OPTIONS = [
    { value: 'business', label: 'Business' },
    { value: 'owner', label: 'Owners' },
    { value: 'documents', label: 'Documents' },
    { value: 'agreement', label: 'Agreement' },
] as const;

/** A document row in a sentence: "Jane Doe: Proof of residential address" for a person's document (BM-120). */
const docName = (d: OnboardingDocument) =>
    d.owner_name ? `${d.owner_name}: ${d.label}` : d.label;

/** BM-120: the rows in the sheet's groups; in the owners group the business's rows first, then one block per person. */
function documentSections(app: StaffOnboardingApplication) {
    const out: {
        key: string;
        title: string;
        person: boolean;
        docs: OnboardingDocument[];
    }[] = [];
    const groups = Object.entries(
        app.document_groups ?? { other: 'Documents' },
    );
    for (const [group, label] of groups) {
        const docs = app.documents.filter(
            (d) => (d.group ?? 'other') === group && d.state !== 'superseded',
        );
        const own = docs.filter((d) => !d.owner_id);
        if (own.length)
            out.push({ key: group, title: label, person: false, docs: own });
        for (const o of app.owners) {
            const theirs = docs.filter((d) => d.owner_id === o.id);
            if (theirs.length)
                out.push({
                    key: `${group}-${o.id}`,
                    title: o.name || 'Unnamed person',
                    person: true,
                    docs: theirs,
                });
        }
    }
    return out;
}

const HISTORY_TITLE: Record<OnboardingHistoryEntry['kind'], string> = {
    submitted: 'Application submitted',
    review_started: 'Review started',
    document_accepted: 'Document accepted',
    document_rejected: 'Document rejected',
    info_requested: 'Information requested',
    resubmitted: 'Application resubmitted',
    approval_requested: 'Approval requested',
    approval_denied: 'Approval not granted',
    approved: 'Approved',
    rejected: 'Application rejected',
    value_revealed: 'Value revealed',
    document_downloaded: 'Document downloaded',
    owner_added: 'Person added',
    owner_updated: 'Person changed',
    owner_removed: 'Person removed',
};

const HISTORY_TONE: Record<
    OnboardingHistoryEntry['kind'],
    NonNullable<TimelineItem['tone']>
> = {
    submitted: 'info',
    review_started: 'info',
    document_accepted: 'success',
    document_rejected: 'danger',
    info_requested: 'warning',
    resubmitted: 'info',
    approval_requested: 'info',
    approval_denied: 'warning',
    approved: 'success',
    rejected: 'danger',
    value_revealed: 'neutral',
    document_downloaded: 'neutral',
    owner_added: 'neutral',
    owner_updated: 'neutral',
    owner_removed: 'warning',
};

const str = (v: unknown): string =>
    typeof v === 'string' || typeof v === 'number' ? String(v) : '';

/** BM-85: what USPS said about the address the merchant typed (the dashboard's Address Validation verdict), as one line for the reviewer. */
function verdictLine(step: unknown): string {
    if (!step || typeof step !== 'object') return '';
    const v = (step as Record<string, unknown>).address_verdict;
    if (!v || typeof v !== 'object') return '';
    const dpv = (v as Record<string, unknown>).dpv_confirmation;
    if (dpv === 'Y') return 'USPS confirmed this address.';
    if (typeof dpv === 'string' && dpv !== '')
        return `USPS did not confirm this address (DPV ${dpv}).`;
    return 'USPS had no verdict on this address.';
}

/** A US address block as one line: "1 Market St, San Francisco, CA 94105". */
function addressLine(value: unknown): string {
    if (!value || typeof value !== 'object') return '';
    const a = value as Record<string, unknown>;
    const city = [
        str(a.city),
        [str(a.state), str(a.postal_code)].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join(', ');
    return [str(a.line1), str(a.line2), city].filter(Boolean).join(', ');
}

/** What a history row says under its title: the note, the person, the field revealed, the document kind. */
function historyDetail(
    e: OnboardingHistoryEntry,
    kindLabel: (type: string) => string,
): ReactNode {
    const data = (e.data ?? {}) as Record<string, unknown>;
    const parts: string[] = [];
    if (e.note) parts.push(e.note);
    if (typeof data.owner_name === 'string' && data.owner_name !== '')
        parts.push(data.owner_name);
    if (typeof data.field === 'string')
        parts.push(data.field === 'ein' ? 'EIN' : 'Date of birth');
    if (typeof data.type === 'string') parts.push(kindLabel(data.type));
    if (typeof data.step === 'string') {
        const step = STEP_OPTIONS.find((s) => s.value === data.step);
        if (step) parts.push(`Step: ${step.label}`);
    }
    return parts.length ? parts.join(' · ') : undefined;
}

/** A masked value with its Reveal button; the clear value shows for `expires_in` seconds and masks again. */
function Masked({
    masked,
    field,
    owner,
    revealed,
    onReveal,
    canReveal,
    busy,
}: {
    masked: string | null | undefined;
    field: OnboardingReveal['field'];
    /** BM-120: a date of birth belongs to a person */
    owner?: OnboardingOwner;
    revealed: string | null;
    onReveal: (field: OnboardingReveal['field'], owner?: string) => void;
    canReveal: boolean;
    busy: boolean;
}) {
    if (!masked) return <span className="text-muted-foreground">—</span>;
    return (
        <span className="inline-flex flex-wrap items-center gap-2">
            <span
                className="font-mono"
                data-testid={
                    owner ? `masked-${field}-${owner.id}` : `masked-${field}`
                }
            >
                {revealed ?? masked}
            </span>
            {canReveal && revealed === null ? (
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    disabled={busy}
                    onClick={() => onReveal(field, owner?.id)}
                    aria-label={
                        field === 'ein'
                            ? 'Reveal EIN'
                            : `Reveal date of birth of ${owner?.name ?? 'the person'}`
                    }
                >
                    <Eye className="size-3.5" aria-hidden="true" />
                    Reveal
                </Button>
            ) : null}
        </span>
    );
}

export function ApplicationReview({
    merchantId,
    app,
    readOnly,
    requestingInfo,
    onRequestingInfo,
}: {
    merchantId: string;
    app: StaffOnboardingApplication;
    readOnly: boolean;
    /** The page's "Request info" button opens the dialog; the dialog closes itself when sent or cancelled. */
    requestingInfo: boolean;
    onRequestingInfo: (open: boolean) => void;
}) {
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const directory = useStaffDirectory();
    const [error, setError] = useState<string | null>(null);
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const invalidate = [
        '/staff/merchants',
        '/staff/queues',
        '/staff/approvals',
    ];
    const base = `/staff/merchants/${merchantId}`;
    const reviewable = isReviewable(app.state) && !readOnly;
    const canReview = reviewable && can(P.onboardingReview);

    // Reveal: the clear value is held here only (never in the query cache) and masked again after expires_in.
    // keyed `ein`, or `date_of_birth:<own_…>` for a person's date of birth (BM-120)
    const [revealed, setRevealed] = useState<
        Partial<Record<string, string | null>>
    >({});
    const revealKey = (field: string, owner?: string) =>
        owner ? `${field}:${owner}` : field;
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    useEffect(() => {
        const t = timers.current;
        return () => Object.values(t).forEach(clearTimeout);
    }, []);
    const reveal = useWrite(
        async (body: { field: OnboardingReveal['field']; owner?: string }) =>
            (
                await api().post<OnboardingReveal>(
                    `${base}/application/reveal`,
                    body.owner
                        ? { field: body.field, owner_id: body.owner }
                        : { field: body.field },
                )
            ).data,
        invalidate,
    );
    const onReveal = (field: OnboardingReveal['field'], owner?: string) =>
        reveal.mutate(
            { field, owner },
            {
                onError,
                onSuccess: (r) => {
                    const key = revealKey(field, owner);
                    setRevealed((v) => ({ ...v, [key]: r.value }));
                    clearTimeout(timers.current[key]);
                    timers.current[key] = setTimeout(
                        () => setRevealed((v) => ({ ...v, [key]: undefined })),
                        Math.max(1, r.expires_in) * 1000,
                    );
                },
            },
        );

    const [decide, setDecide] = useState<{
        document: OnboardingDocument;
        decision: 'accepted' | 'rejected';
    } | null>(null);
    const [note, setNote] = useState('');
    const documentReview = useWrite(
        async (body: {
            document: string;
            decision: 'accepted' | 'rejected';
            note: string;
        }) =>
            (
                await api().post<OnboardingDocument>(
                    `${base}/documents/${body.document}/review`,
                    { decision: body.decision, note: body.note },
                )
            ).data,
        invalidate,
    );

    const [ask, setAsk] = useState<{
        note: string;
        step: MerchantReviewRequest['step'];
        document_types: string[];
    }>({ note: '', step: 'documents', document_types: [] });
    const requestInfo = useWrite(
        async (body: MerchantReviewRequest) =>
            (await api().post<MerchantReview>(`${base}/review`, body)).data,
        invalidate,
    );

    const business = app.business as Record<string, unknown>;
    const review = (app.merchant as Record<string, unknown>)?.address_review as
        Record<string, unknown> | null | undefined;
    const previousAddress = review
        ? addressLine(review.previous_address) || 'none'
        : '';
    const kindLabel = (type: string) =>
        app.documents.find((d) => d.type === type)?.label ?? type;
    const canRevealValues = !readOnly && can(P.onboardingReview);
    const entityType =
        app.business_types.find((t) => t.value === business.type)?.label ??
        str(business.type);
    const history: TimelineItem[] = app.history.map((e) => ({
        id: e.id,
        at: e.at,
        actor: whoName(e.by, me, directory),
        title: HISTORY_TITLE[e.kind] ?? e.kind,
        detail: historyDetail(e, kindLabel),
        tone: HISTORY_TONE[e.kind] ?? 'neutral',
    }));

    return (
        <div className="space-y-4" data-testid="application-review">
            {readOnly ? (
                <Banner tone="info" title="Read only for acquirer review">
                    You can see the application, the documents and the dispute
                    history. Decisions stay with the tenant&apos;s staff.
                </Banner>
            ) : null}
            {error ? (
                <p
                    role="alert"
                    className="text-destructive text-sm"
                    data-testid="application-error"
                >
                    {error}
                </p>
            ) : null}
            <Card>
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-sm font-medium">
                        Application
                    </CardTitle>
                    <StatusBadge
                        status={app.state}
                        domain={ADMIN_STATUSES.application}
                    />
                </CardHeader>
                <CardContent>
                    <FieldGrid>
                        <DetailRow
                            label="Legal name"
                            value={str(business.legal_name) || '—'}
                            layout="stacked"
                        />
                        <DetailRow
                            label="Entity type"
                            value={entityType || '—'}
                            layout="stacked"
                        />
                        <DetailRow label="EIN" layout="stacked">
                            <Masked
                                masked={app.business.ein_masked}
                                field="ein"
                                revealed={revealed.ein ?? null}
                                onReveal={onReveal}
                                canReveal={canRevealValues}
                                busy={reveal.isPending}
                            />
                        </DetailRow>
                        <DetailRow label="Business address" layout="stacked">
                            {addressLine(business.address) || '—'}
                            {verdictLine(business) ? (
                                <span
                                    className="text-muted-foreground block text-xs"
                                    data-testid="business-address-verdict"
                                >
                                    {verdictLine(business)}
                                </span>
                            ) : null}
                            {/* BM-85 decision 25: an approved merchant changed its address; this review decides it */}
                            {previousAddress ? (
                                <span
                                    className="text-status-warning block text-xs"
                                    data-testid="business-address-previous"
                                >
                                    Address change under review. Previous:{' '}
                                    {previousAddress}
                                </span>
                            ) : null}
                        </DetailRow>
                        {/* BM-65 decision 21: verified with this review (beside the bank proof); a change after approval is reviewed here */}
                        <BankAccountRow merchantId={merchantId} />
                        <DetailRow
                            label="Website"
                            value={str(business.website) || '—'}
                            layout="stacked"
                        />
                        <DetailRow
                            label="Support email"
                            value={str(business.support_email) || '—'}
                            layout="stacked"
                        />
                        <DetailRow
                            label="Statement descriptor"
                            value={
                                str(app.descriptor ?? business.descriptor) ||
                                '—'
                            }
                            layout="stacked"
                        />
                        <DetailRow label="Agreement" layout="stacked">
                            {app.agreement.accepted_at ? (
                                <>
                                    Version{' '}
                                    {app.agreement.accepted_version ??
                                        app.agreement.version}
                                    , accepted{' '}
                                    <DateTime
                                        zone="inline"
                                        value={app.agreement.accepted_at}
                                        empty="—"
                                    />
                                </>
                            ) : (
                                'Not accepted'
                            )}
                        </DetailRow>
                        <DetailRow label="Submitted" layout="stacked">
                            <DateTime
                                zone="inline"
                                value={app.submitted_at}
                                empty="—"
                            />
                        </DetailRow>
                        <DetailRow label="Review started" layout="stacked">
                            {app.review_started_at ? (
                                <>
                                    <DateTime
                                        zone="inline"
                                        value={app.review_started_at}
                                        empty="—"
                                    />
                                    <span className="text-muted-foreground ml-1 text-xs">
                                        by{' '}
                                        {whoName(
                                            app.review_started_by,
                                            me,
                                            directory,
                                        )}
                                    </span>
                                </>
                            ) : (
                                '—'
                            )}
                        </DetailRow>
                        {app.decided_at ? (
                            <DetailRow label="Decided" layout="stacked">
                                <DateTime
                                    zone="inline"
                                    value={app.decided_at}
                                    empty="—"
                                />
                                <span className="text-muted-foreground ml-1 text-xs">
                                    by {whoName(app.decided_by, me, directory)}
                                </span>
                            </DetailRow>
                        ) : null}
                        {app.decision_note ? (
                            <DetailRow
                                label="Decision note"
                                value={app.decision_note}
                                layout="stacked"
                            />
                        ) : null}
                    </FieldGrid>
                    <OwnersList
                        owners={app.owners}
                        revealed={(o) =>
                            revealed[revealKey('date_of_birth', o.id)] ?? null
                        }
                        onReveal={onReveal}
                        canReveal={canRevealValues}
                        busy={reveal.isPending}
                    />
                    {app.state === 'needs_info' && app.needs_info ? (
                        <p className="text-muted-foreground mt-4 text-xs">
                            Waiting for the merchant: {app.needs_info.note}
                        </p>
                    ) : null}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-sm font-medium">
                        Documents
                    </CardTitle>
                    {app.rejected_documents > 0 ? (
                        <span className="text-destructive text-xs">
                            {app.rejected_documents} rejected: approve is
                            refused until the merchant replaces it
                        </span>
                    ) : null}
                </CardHeader>
                <CardContent>
                    <div className="space-y-4" data-testid="documents">
                        {documentSections(app).map((section) => (
                            <section
                                key={section.key}
                                data-testid={`documents-${section.key}`}
                            >
                                <h3
                                    className={
                                        section.person
                                            ? 'text-muted-foreground text-xs font-medium'
                                            : 'text-sm font-medium'
                                    }
                                >
                                    {section.title}
                                </h3>
                                <ul className="divide-y text-sm">
                                    {section.docs.map((d) => (
                                        <li
                                            key={d.id ?? d.key ?? d.type}
                                            className="flex flex-wrap items-center justify-between gap-2 py-2"
                                            data-testid={`document-${d.key ?? d.type}`}
                                        >
                                            <span className="min-w-0 space-y-0.5">
                                                <span className="flex flex-wrap items-center gap-2">
                                                    <span className="font-medium">
                                                        {d.label}
                                                    </span>
                                                    <StatusBadge
                                                        status={d.state}
                                                        domain={
                                                            ADMIN_STATUSES.document
                                                        }
                                                    />
                                                </span>
                                                {d.file_name ? (
                                                    <span className="text-muted-foreground block text-xs">
                                                        <span className="font-mono">
                                                            {d.file_name}
                                                        </span>
                                                        {d.uploaded_at ? (
                                                            <>
                                                                ,{' '}
                                                                <DateTime
                                                                    value={
                                                                        d.uploaded_at
                                                                    }
                                                                    empty="—"
                                                                />
                                                            </>
                                                        ) : null}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted-foreground block text-xs">
                                                        Not uploaded
                                                    </span>
                                                )}
                                                {d.note ? (
                                                    <span className="block text-xs">
                                                        {d.note}
                                                    </span>
                                                ) : null}
                                            </span>
                                            <span className="flex flex-wrap items-center gap-1">
                                                {d.id && d.file_name ? (
                                                    <Button
                                                        asChild
                                                        size="sm"
                                                        variant="ghost"
                                                    >
                                                        <a
                                                            href={`/api${base}/documents/${d.id}/file`}
                                                            download={
                                                                d.file_name
                                                            }
                                                        >
                                                            <Download
                                                                className="size-3.5"
                                                                aria-hidden="true"
                                                            />
                                                            Download
                                                        </a>
                                                    </Button>
                                                ) : null}
                                                {canReview &&
                                                d.id &&
                                                (d.state === 'uploaded' ||
                                                    d.state === 'rejected') ? (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            documentReview.mutate(
                                                                {
                                                                    document:
                                                                        d.id as string,
                                                                    decision:
                                                                        'accepted',
                                                                    note: '',
                                                                },
                                                                { onError },
                                                            )
                                                        }
                                                    >
                                                        Accept
                                                    </Button>
                                                ) : null}
                                                {canReview &&
                                                d.id &&
                                                (d.state === 'uploaded' ||
                                                    d.state === 'accepted') ? (
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => {
                                                            setNote('');
                                                            setDecide({
                                                                document: d,
                                                                decision:
                                                                    'rejected',
                                                            });
                                                        }}
                                                    >
                                                        Reject
                                                    </Button>
                                                ) : null}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="text-sm font-medium">
                        Review history
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {history.length ? (
                        <Timeline items={history} />
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            Nothing yet.
                        </p>
                    )}
                </CardContent>
            </Card>

            {canReview ? (
                <RequestInfoDialog
                    rows={app.documents.filter((d) => d.required === true)}
                    open={requestingInfo}
                    value={ask}
                    onChange={setAsk}
                    busy={requestInfo.isPending}
                    error={requestInfo.error}
                    onOpenChange={(o) => {
                        if (!o) {
                            onRequestingInfo(false);
                            requestInfo.reset();
                        }
                    }}
                    onConfirm={() =>
                        requestInfo.mutate(
                            {
                                state: 'needs_info',
                                note: ask.note.trim(),
                                step: ask.step,
                                document_types:
                                    ask.step === 'documents'
                                        ? (ask.document_types as MerchantReviewRequest['document_types'])
                                        : [],
                            },
                            {
                                onSuccess: () => {
                                    onRequestingInfo(false);
                                    setAsk({
                                        note: '',
                                        step: 'documents',
                                        document_types: [],
                                    });
                                },
                            },
                        )
                    }
                />
            ) : null}
            {decide ? (
                <ConfirmDialog
                    open
                    onOpenChange={(o) => {
                        if (!o) {
                            setDecide(null);
                            documentReview.reset();
                        }
                    }}
                    title={`Reject ${docName(decide.document)}?`}
                    description="The merchant sees your note on the document and must upload a new file before the application can be approved."
                    confirmLabel="Reject document"
                    destructive
                    busy={documentReview.isPending}
                    onConfirm={() =>
                        documentReview.mutate(
                            {
                                document: decide.document.id as string,
                                decision: 'rejected',
                                note: note.trim(),
                            },
                            { onSuccess: () => setDecide(null) },
                        )
                    }
                >
                    <div className="space-y-1" data-testid="document-note">
                        <Label htmlFor="document-note">
                            What is wrong with it
                        </Label>
                        <Textarea
                            id="document-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                        />
                        <p className="text-muted-foreground text-xs">
                            The merchant sees this text.
                        </p>
                        {documentReview.error ? (
                            <p
                                role="alert"
                                className="text-destructive text-xs"
                            >
                                {apiErrorMessage(documentReview.error)}
                            </p>
                        ) : null}
                    </div>
                </ConfirmDialog>
            ) : null}
        </div>
    );
}

/** BM-120: the people on the application, each with a masked date of birth and an audited Reveal. */
/** BM-65 decision 21: the payout bank account the merchant entered: holder, bank, last 4 and its state. */
function BankAccountRow({ merchantId }: { merchantId: string }) {
    const query = useOne<MerchantBankAccount | null>(
        `/staff/merchants/${merchantId}/bank-account`,
    );
    const a = query.data?.data ?? null;
    return (
        <DetailRow label="Payout bank account" layout="stacked">
            <span data-testid="review-bank-account">
                {a ? (
                    <>
                        {a.holder} · {a.bank_name ?? 'Bank not named'} ·{' '}
                        {a.account_type === 'savings' ? 'Savings' : 'Checking'}{' '}
                        •••• {a.display_last4}{' '}
                        <StatusBadge status={a.state} domain="bank_account" />
                        {a.failure_reason ? (
                            <span className="text-muted-foreground block text-xs">
                                {a.failure_reason}
                            </span>
                        ) : null}
                    </>
                ) : query.isPending ? null : (
                    'Not entered'
                )}
            </span>
        </DetailRow>
    );
}

function OwnersList({
    owners,
    revealed,
    onReveal,
    canReveal,
    busy,
}: {
    owners: readonly OnboardingOwner[];
    revealed: (o: OnboardingOwner) => string | null;
    onReveal: (field: OnboardingReveal['field'], owner?: string) => void;
    canReveal: boolean;
    busy: boolean;
}) {
    return (
        <section className="mt-4 space-y-2" data-testid="owners">
            <h3 className="text-sm font-medium">Owners and control person</h3>
            {owners.length === 0 ? (
                <p className="text-muted-foreground text-sm">Nobody added.</p>
            ) : (
                <ul className="divide-y text-sm">
                    {owners.map((o) => (
                        <li
                            key={o.id}
                            className="space-y-2 py-2"
                            data-testid={`owner-${o.id}`}
                        >
                            <span className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">
                                    {o.name || 'Unnamed person'}
                                </span>
                                {o.is_control_person ? (
                                    <Badge variant="neutral" size="sm">
                                        Control person
                                    </Badge>
                                ) : null}
                            </span>
                            <FieldGrid>
                                <DetailRow
                                    label="Date of birth"
                                    layout="stacked"
                                >
                                    <Masked
                                        masked={o.date_of_birth_masked}
                                        field="date_of_birth"
                                        owner={o}
                                        revealed={revealed(o)}
                                        onReveal={onReveal}
                                        canReveal={canReveal}
                                        busy={busy}
                                    />
                                </DetailRow>
                                <DetailRow
                                    label="Ownership"
                                    value={
                                        o.ownership_percent == null
                                            ? '—'
                                            : `${o.ownership_percent}%`
                                    }
                                    layout="stacked"
                                />
                                <DetailRow
                                    label="Photo ID"
                                    value={
                                        o.id_type === 'passport'
                                            ? 'Passport'
                                            : "State ID or driver's licence"
                                    }
                                    layout="stacked"
                                />
                                <DetailRow
                                    label="Home address"
                                    layout="stacked"
                                >
                                    {addressLine(o.address) || '—'}
                                    {verdictLine(o) ? (
                                        <span
                                            className="text-muted-foreground block text-xs"
                                            data-testid={`home-address-verdict-${o.id}`}
                                        >
                                            {verdictLine(o)}
                                        </span>
                                    ) : null}
                                </DetailRow>
                            </FieldGrid>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function RequestInfoDialog({
    rows,
    open,
    value,
    onChange,
    busy,
    error,
    onOpenChange,
    onConfirm,
}: {
    /** BM-120: this application's document rows; a person's row asks for that person's file */
    rows: OnboardingDocument[];
    open: boolean;
    value: {
        note: string;
        step: MerchantReviewRequest['step'];
        document_types: string[];
    };
    onChange: (v: {
        note: string;
        step: MerchantReviewRequest['step'];
        document_types: string[];
    }) => void;
    busy: boolean;
    error: unknown;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
}) {
    const [touched, setTouched] = useState(false);
    const missing = value.note.trim().length < 3;
    return (
        <ConfirmDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Ask the merchant for more information?"
            description="The application moves to Needs info and the merchant is emailed your note with a link to the step."
            confirmLabel="Request info"
            busy={busy}
            onConfirm={() => {
                setTouched(true);
                if (!missing) onConfirm();
            }}
        >
            <div className="space-y-3" data-testid="request-info">
                <div className="space-y-1">
                    <Label htmlFor="request-note">What is missing</Label>
                    <Textarea
                        id="request-note"
                        value={value.note}
                        onChange={(e) =>
                            onChange({ ...value, note: e.target.value })
                        }
                        rows={3}
                    />
                    <p className="text-muted-foreground text-xs">
                        The merchant sees this text.
                    </p>
                    {touched && missing ? (
                        <p role="alert" className="text-destructive text-xs">
                            Write what is missing (at least 3 characters).
                        </p>
                    ) : null}
                </div>
                <SearchableSelect
                    label="Step to fix"
                    options={optionsOf(STEP_OPTIONS)}
                    value={value.step ?? 'documents'}
                    onChange={(v) =>
                        onChange({
                            ...value,
                            step: (v ??
                                'documents') as MerchantReviewRequest['step'],
                            document_types:
                                v === 'documents' ? value.document_types : [],
                        })
                    }
                />
                {value.step === 'documents' ? (
                    <fieldset className="space-y-1">
                        <legend className="text-sm font-medium">
                            Documents that need a new file
                        </legend>
                        {rows
                            .map((d) => ({
                                value: d.key ?? d.type,
                                label: docName(d),
                            }))
                            .map((k) => (
                                <label
                                    key={k.value}
                                    className="flex items-center gap-2 text-sm"
                                >
                                    <Checkbox
                                        checked={value.document_types.includes(
                                            k.value,
                                        )}
                                        onCheckedChange={(on) =>
                                            onChange({
                                                ...value,
                                                document_types:
                                                    on === true
                                                        ? [
                                                              ...value.document_types,
                                                              k.value,
                                                          ]
                                                        : value.document_types.filter(
                                                              (t) =>
                                                                  t !== k.value,
                                                          ),
                                            })
                                        }
                                    />
                                    {k.label}
                                </label>
                            ))}
                    </fieldset>
                ) : null}
                {error ? (
                    <p role="alert" className="text-destructive text-xs">
                        {apiErrorMessage(error)}
                    </p>
                ) : null}
            </div>
        </ConfirmDialog>
    );
}
